import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Store } from 'lucide-react';
import { Button, Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import {
  DataTable,
  EmptyState,
  Pagination,
  StatusPill,
  type ColumnDef,
  type FilterCondition,
  type FilterField,
} from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useResellers, type ResellerRow } from '@/api/resellers';
import { paginationView } from '@/pages/customers/pagination-summary';
import { EntityLink } from '@/components/entity-link';
import { formatDate } from '@/lib/format';
import { ResellerListToolbar, colId } from './reseller-list-toolbar';
import { resellerStatusTone, resellerTierTone, humanize } from './reseller-tones';

const PAGE_SIZE = 20;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

function conditionsToFilters(conditions: FilterCondition[]): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const c of conditions) {
    if (!c.value) continue;
    const values = c.value.split(',').map((s) => s.trim()).filter(Boolean);
    out[c.field] = [...(out[c.field] ?? []), ...values];
  }
  return out;
}

const FILTER_FIELDS: FilterField[] = [
  {
    value: 'status',
    label: 'Status',
    input: 'select',
    options: [
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
      { label: 'Suspended', value: 'suspended' },
    ],
  },
  {
    value: 'tier',
    label: 'Tier',
    input: 'select',
    options: [
      { label: 'Bronze', value: 'bronze' },
      { label: 'Silver', value: 'silver' },
      { label: 'Gold', value: 'gold' },
      { label: 'Platinum', value: 'platinum' },
    ],
  },
];

function useResellerColumns(navigate: ReturnType<typeof useNavigate>): ColumnDef<ResellerRow, unknown>[] {
  return useMemo(() => [
    {
      id: 'reseller_name',
      header: 'Reseller',
      cell: ({ row }) => (
        <EntityLink
          to={`/resellers/${row.original.reseller_id}`}
          id={row.original.reseller_id}
          name={row.original.reseller_name}
        />
      ),
    },
    {
      id: 'customer',
      header: 'Linked Customer',
      cell: ({ row }) => {
        const c = row.original.customer;
        return c ? (
          <span title={c.customer_email} style={{ fontSize: 'var(--vn-font-size-sm)' }}>
            {c.customer_name}
          </span>
        ) : <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>;
      },
    },
    {
      id: 'reseller_tier',
      header: 'Tier',
      cell: ({ row }) => row.original.reseller_tier ? (
        <StatusPill tone={resellerTierTone(row.original.reseller_tier)}>
          {humanize(row.original.reseller_tier)}
        </StatusPill>
      ) : <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>,
    },
    {
      id: 'reseller_status',
      header: 'Status',
      cell: ({ row }) => (
        <StatusPill tone={resellerStatusTone(row.original.reseller_status)}>
          {humanize(row.original.reseller_status)}
        </StatusPill>
      ),
    },
    {
      id: 'organization_count',
      header: () => <div style={{ textAlign: 'right' }}>Orgs</div>,
      cell: ({ row }) => (
        <div style={{ textAlign: 'right' }}>{row.original.organization_count}</div>
      ),
    },
    {
      id: 'created_at',
      header: 'Created',
      cell: ({ row }) => formatDate(row.original.created_at),
    },
  ], [navigate]);
}

/**
 * Server-paginated reseller list: Toolbar (search + Filters + Display options)
 * → DataTable with row-click navigation → Pagination. Mirrors the
 * `ContractListSection` pattern from ui-conventions §3.
 */
export function ResellerListSection() {
  const navigate = useNavigate();
  const columns = useResellerColumns(navigate);

  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);
  const [filterOpen, setFilterOpen] = useState(false);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const [visible, setVisible] = useState<Record<string, boolean>>(
    () => Object.fromEntries(columns.map((c) => [colId(c), true])),
  );

  const filters = useMemo(() => conditionsToFilters(conditions), [conditions]);

  const { data, isLoading, isError, isFetching, refetch } = useResellers({
    page,
    page_size: PAGE_SIZE,
    q: query || undefined,
    filters: Object.keys(filters).length ? filters : undefined,
  });

  const rows = data?.results ?? [];
  const pagination = data?.pagination;
  const pageCount = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? rows.length;

  const visibleColumns = useMemo(
    () => columns.filter((c) => visible[colId(c)] !== false),
    [columns, visible],
  );

  const onSearch = (value: string) => {
    setRawQuery(value);
    setPage(1);
  };

  const applyFilters = (next: FilterCondition[]) => {
    setConditions(next.filter((c) => c.value !== ''));
    setPage(1);
    setFilterOpen(false);
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <ResellerListToolbar
        columns={columns}
        filterFields={FILTER_FIELDS}
        searchPlaceholder="Search resellers…"
        query={rawQuery}
        onQueryChange={onSearch}
        conditions={conditions}
        filterOpen={filterOpen}
        onFilterOpenChange={setFilterOpen}
        onApplyFilters={applyFilters}
        visible={visible}
        onToggleColumn={(id, checked) => setVisible((prev) => ({ ...prev, [id]: checked }))}
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Failed to load resellers"
          actions={
            <Button size="sm" variant="ghost" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : isLoading ? (
        <Card flat>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 24 }} />
            ))}
          </CardBody>
        </Card>
      ) : (
        <DataTable
          columns={visibleColumns}
          data={rows}
          getRowId={(r) => String(r.reseller_id)}
          onRowClick={(r) => navigate(`/resellers/${r.reseller_id}`)}
          emptyState={
            <EmptyState
              icon={<Store size={24} />}
              title="No resellers found"
              description="Try adjusting your search or filters."
            />
          }
          footer={(() => {
            const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
            return (
              <Pagination
                page={page}
                pageCount={view.pageCount}
                onPageChange={setPage}
                summary={`${view.summary}${isFetching ? ' …' : ''}`}
              />
            );
          })()}
        />
      )}
    </div>
  );
}
